import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { Typography, Button, Divider, Grid } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Country, State, ICountry, IState } from 'country-state-city';

// Type definitions for component props and form data
type InputFieldProps = {
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  isMandatory?: boolean;
  error?: string;
  type?: string;
  isDisabled?: boolean;
};

type TextAreaFieldProps = {
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  isMandatory?: boolean;
};

type DropdownFieldProps = {
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  options: Array<{ value: string; label: string }>;
  isMandatory?: boolean;
  error?: string;
  isDisabled?: boolean;
};

type FormData = {
  companyName: string;
  companyType: string;
  crn: string;
  description: string;
  deviceTypes: string;
  landline: string;
  mobile: string;
  email: string;
  website: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  fullAddress: string;
  selectedFile?: File | null;
};

type Errors = Partial<Record<keyof FormData, string>>;

// Input Field Component
const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  isMandatory = false,
  error = '',
  type = 'text',
  isDisabled = false,
}) => (
  <div className="flex flex-col mb-4">
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 600, fontSize: '14px', mb: 0.5, display: 'flex', alignItems: 'center' }}
    >
      {label}
      {isMandatory && <span className="text-red-500 ml-1">*</span>}
    </Typography>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={isDisabled}
      className={`
        w-full p-2.5 rounded-lg border
        font-['Inter Tight'] text-sm outline-none transition-colors
        ${error ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}
        ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
      `}
    />
    {error && (
      <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
        {error}
      </Typography>
    )}
  </div>
);

// Textarea Field Component
const TextAreaField: React.FC<TextAreaFieldProps> = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  isMandatory = false,
}) => (
  <div className="flex flex-col mb-4">
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 600, fontSize: '14px', mb: 0.5, display: 'flex', alignItems: 'center' }}
    >
      {label}
      {isMandatory && <span className="text-red-500 ml-1">*</span>}
    </Typography>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={4}
      className="
        w-full p-2.5 rounded-lg border border-gray-300
        font-['Inter Tight'] text-sm resize-y outline-none transition-colors
        focus:border-blue-500
      "
    />
  </div>
);

// Dropdown Field Component
const DropdownField: React.FC<DropdownFieldProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  isMandatory = false,
  error = '',
  isDisabled = false,
}) => (
  <div className="flex flex-col mb-4">
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 600, fontSize: '14px', mb: 0.5, display: 'flex', alignItems: 'center' }}
    >
      {label}
      {isMandatory && <span className="text-red-500 ml-1">*</span>}
    </Typography>
    <select
      name={name}
      value={value}
      onChange={onChange}
      disabled={isDisabled}
      className={`
        w-full p-2.5 rounded-lg border
        font-['Inter Tight'] text-sm bg-white outline-none transition-colors
        ${error ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'}
        ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : ''}
      `}
    >
      <option value="">Select {label}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {error && (
      <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
        {error}
      </Typography>
    )}
  </div>
);

// Main Component
const EditCompany: React.FC = () => {
  // State Management
  const [formData, setFormData] = useState<FormData>({
    companyName: '',
    companyType: '',
    crn: '',
    description: '',
    deviceTypes: '',
    landline: '',
    mobile: '',
    email: '',
    website: '',
    city: '',
    state: '',
    pincode: '',
    country: '',
    fullAddress: '',
    selectedFile: null,
  });

  const [errors, setErrors] = useState<Errors>({});
  const [countries, setCountries] = useState<ICountry[]>([]);
  const [states, setStates] = useState<IState[]>([]);

  // Effects
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);

  useEffect(() => {
    if (formData.country) {
      const countryStates = State.getStatesOfCountry(formData.country);
      setStates(countryStates);
      // Reset state when country changes
      setFormData(prev => ({ ...prev, state: '' }));
    } else {
      setStates([]);
    }
  }, [formData.country]);

  // Event Handlers
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, selectedFile: file }));
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newErrors: Errors = {};
    if (!formData.companyName) newErrors.companyName = 'Company Name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.website) newErrors.website = 'Website is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.pincode) newErrors.pincode = 'Pincode is required';
    if (!formData.deviceTypes) newErrors.deviceTypes = 'Device Type is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
    } else {
      setErrors({});
      // Add form submission logic here
      console.log('Form Submitted', formData);
    }
  };

  return (
    <div className="flex flex-col items-center w-full min-h-screen">
      <div className="w-full px-4 md:px-8">
        {/* Back Button */}
        <div className="flex justify-start items-center mb-6">
          <Button
            className="text-blue-600 font-medium flex items-center"
            onClick={() => window.history.back()}
            sx={{ display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'none' }}
          >
            <ArrowBack className="mr-1" /> Back
          </Button>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: '24px', lineHeight: '36px' }}>
            EDIT COMPANY
          </Typography>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Basic Info Section */}
          <div className="mb-6">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '18px', mb: 2 }}>
              Basic Info
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Company Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter company name..."
                  isMandatory
                  error={errors.companyName}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Type"
                  name="companyType"
                  value={formData.companyType}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter company type..."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <div className="flex flex-col mb-4">
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, fontSize: '14px', mb: 0.5, display: 'flex', alignItems: 'center' }}
                  >
                    Add Company Logo
                  </Typography>
                  <input
                    type="file"
                    name="selectedFile"
                    onChange={handleFileChange}
                    className="w-full p-2.5 rounded-lg border border-gray-300 font-['Inter Tight'] text-sm bg-white outline-none transition-colors focus:border-blue-500"
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextAreaField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange as (e: ChangeEvent<HTMLTextAreaElement>) => void}
                  placeholder="Enter Company Description..."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DropdownField
                  label="Device Types"
                  name="deviceTypes"
                  value={formData.deviceTypes}
                  onChange={handleChange as (e: ChangeEvent<HTMLSelectElement>) => void}
                  options={[
                    { value: 'android', label: 'Android' },
                    { value: 'vp1', label: 'VP1' }
                  ]}
                  isMandatory
                  error={errors.deviceTypes}
                />
              </Grid>
            </Grid>
          </div>

          <Divider sx={{ my: 4 }} />

          {/* Contacts Section */}
          <div className="mb-6">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '18px', mb: 2 }}>
              Contacts
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Landline Phone No"
                  name="landline"
                  value={formData.landline}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter Landline phone No..."
                  type="tel"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Mobile No"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter Mobile No..."
                  type="tel"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter Email..."
                  isMandatory
                  error={errors.email}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputField
                  label="Website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter website url"
                  isMandatory
                  error={errors.website}
                />
              </Grid>
            </Grid>
          </div>

          <Divider sx={{ my: 4 }} />

          {/* Address Section */}
          <div className="mb-6">
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '18px', mb: 2 }}>
              Address
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DropdownField
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange as (e: ChangeEvent<HTMLSelectElement>) => void}
                  options={countries.map(country => ({
                    value: country.isoCode,
                    label: country.name
                  }))}
                  error={errors.country}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DropdownField
                  label="State"
                  name="state"
                  value={formData.state}
                  onChange={handleChange as (e: ChangeEvent<HTMLSelectElement>) => void}
                  options={states.map(state => ({
                    value: state.isoCode,
                    label: state.name
                  }))}
                  isDisabled={!formData.country}
                  error={errors.state}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputField
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter City..."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputField
                  label="Pincode"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange as (e: ChangeEvent<HTMLInputElement>) => void}
                  placeholder="Enter Pincode..."
                  error={errors.pincode}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextAreaField
                  label="Full Address"
                  name="fullAddress"
                  value={formData.fullAddress}
                  onChange={handleChange as (e: ChangeEvent<HTMLTextAreaElement>) => void}
                  placeholder="Enter Company Full Address..."
                />
              </Grid>
            </Grid>
          </div>

          {/* Form Actions */}
          <div className="relative w-full mt-auto">
            <div className="flex flex-col md:flex-row justify-between px-8 py-4 border-t bg-white">
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => window.history.back()}
                sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '21px', textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '21px',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  boxShadow: 1,
                  textTransform: 'none',
                }}
              >
                SUBMIT
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCompany;